import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountBalance,
  TrendingUp,
  TrendingDown,
  ShowChart,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import {
  apiService,
  TradingBucket,
  Position,
  DashboardSummary,
  BucketCreate,
} from '../services/api';
import AutoTrading from './AutoTrading';

const TradingDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [buckets, setBuckets] = useState<TradingBucket[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newBucket, setNewBucket] = useState<BucketCreate>({
    name: '',
    strategy_type: 'price_range',
    allocated_capital: 10000,
    max_positions: 5,
    max_position_size: 2000,
    stop_loss_pct: 0.05,
    take_profit_pct: 0.10,
    mode: 'paper',
  });

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, bucketsData, positionsData] = await Promise.all([
        apiService.getDashboardSummary(),
        apiService.listBuckets(),
        apiService.listPositions(undefined, 'open'),
      ]);
      setSummary(summaryData);
      setBuckets(bucketsData);
      setPositions(positionsData);
    } catch (err) {
      console.error('Failed to load trading data:', err);
      setError('Failed to load trading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateBucket = async () => {
    try {
      await apiService.createBucket(newBucket);
      setCreateDialogOpen(false);
      setNewBucket({
        name: '',
        strategy_type: 'price_range',
        allocated_capital: 10000,
        max_positions: 5,
        max_position_size: 2000,
        stop_loss_pct: 0.05,
        take_profit_pct: 0.10,
        mode: 'paper',
      });
      loadData();
    } catch (err) {
      console.error('Failed to create bucket:', err);
      setError('Failed to create bucket. Please try again.');
    }
  };

  const handleDeleteBucket = async (bucketId: number) => {
    if (!window.confirm('Are you sure you want to delete this bucket?')) {
      return;
    }
    try {
      await apiService.deleteBucket(bucketId);
      loadData();
    } catch (err) {
      console.error('Failed to delete bucket:', err);
      setError('Failed to delete bucket. Please try again.');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'disabled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getModeColor = (mode: string) => {
    return mode === 'paper' ? 'info' : 'warning';
  };

  if (loading && !summary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>
          Trading Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Bucket
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Manual Trading" />
          <Tab label="Auto-Trading" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccountBalance color="primary" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Total Capital
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {formatCurrency(summary.summary.total_allocated_capital)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Available: {formatCurrency(summary.summary.total_available_capital)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  {summary.summary.total_unrealized_pnl >= 0 ? (
                    <TrendingUp color="success" sx={{ mr: 1 }} />
                  ) : (
                    <TrendingDown color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Total P&L
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color={summary.summary.total_unrealized_pnl >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(summary.summary.total_unrealized_pnl)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Utilization: {summary.summary.capital_utilization_pct.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ShowChart color="info" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Open Positions
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {summary.summary.total_open_positions}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Across {summary.summary.total_buckets} buckets
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccountBalance color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Total Buckets
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {summary.summary.total_buckets}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Top: {summary.top_buckets.length} active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Trading Buckets */}
      <Paper sx={{ mb: 3 }}>
        <Box p={2} borderBottom={1} borderColor="divider">
          <Typography variant="h6" fontWeight={600}>
            Trading Buckets
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Strategy</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Capital</TableCell>
                <TableCell align="right">Available</TableCell>
                <TableCell align="right">Positions</TableCell>
                <TableCell align="right">P&L</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {buckets.map((bucket) => (
                <TableRow key={bucket.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {bucket.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bucket.strategy_type.replace('_', ' ').toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bucket.mode.toUpperCase()}
                      size="small"
                      color={getModeColor(bucket.mode)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bucket.status.toUpperCase()}
                      size="small"
                      color={getStatusColor(bucket.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(bucket.allocated_capital)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(bucket.available_capital)}
                  </TableCell>
                  <TableCell align="right">
                    {bucket.open_positions} / {bucket.max_positions}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: bucket.total_pnl >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 600,
                    }}
                  >
                    {formatCurrency(bucket.total_pnl)}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={bucket.status === 'active' ? 'Pause' : 'Resume'}>
                        <IconButton size="small" color="warning">
                          {bucket.status === 'active' ? (
                            <PauseIcon fontSize="small" />
                          ) : (
                            <PlayIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBucket(bucket.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {buckets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No trading buckets. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Open Positions */}
      <Paper>
        <Box p={2} borderBottom={1} borderColor="divider">
          <Typography variant="h6" fontWeight={600}>
            Open Positions
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Entry Price</TableCell>
                <TableCell align="right">Current Price</TableCell>
                <TableCell align="right">Market Value</TableCell>
                <TableCell align="right">P&L</TableCell>
                <TableCell align="right">P&L %</TableCell>
                <TableCell align="right">Days Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {positions.map((position) => (
                <TableRow key={position.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {position.symbol}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={position.direction.toUpperCase()}
                      size="small"
                      color={position.is_long ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{position.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(position.entry_price)}</TableCell>
                  <TableCell align="right">
                    {position.current_price ? formatCurrency(position.current_price) : '-'}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(position.market_value)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        (position.unrealized_pnl || 0) >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 600,
                    }}
                  >
                    {position.unrealized_pnl ? formatCurrency(position.unrealized_pnl) : '-'}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        (position.unrealized_pnl_pct || 0) >= 0
                          ? 'success.main'
                          : 'error.main',
                    }}
                  >
                    {position.unrealized_pnl_pct
                      ? formatPercent(position.unrealized_pnl_pct)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">{position.days_open}</TableCell>
                </TableRow>
              ))}
              {positions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No open positions.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Bucket Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Trading Bucket</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Bucket Name"
              value={newBucket.name}
              onChange={(e) => setNewBucket({ ...newBucket, name: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Strategy Type</InputLabel>
              <Select
                value={newBucket.strategy_type}
                label="Strategy Type"
                onChange={(e) =>
                  setNewBucket({ ...newBucket, strategy_type: e.target.value })
                }
              >
                <MenuItem value="price_range">Price Range Trading</MenuItem>
                <MenuItem value="scoring">Stock Scoring</MenuItem>
                <MenuItem value="ml_prediction">ML Prediction</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Mode</InputLabel>
              <Select
                value={newBucket.mode}
                label="Mode"
                onChange={(e) => setNewBucket({ ...newBucket, mode: e.target.value })}
              >
                <MenuItem value="paper">Paper Trading</MenuItem>
                <MenuItem value="live">Live Trading</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Allocated Capital"
              type="number"
              value={newBucket.allocated_capital}
              onChange={(e) =>
                setNewBucket({ ...newBucket, allocated_capital: parseFloat(e.target.value) })
              }
              fullWidth
              required
            />
            <TextField
              label="Max Positions"
              type="number"
              value={newBucket.max_positions}
              onChange={(e) =>
                setNewBucket({ ...newBucket, max_positions: parseInt(e.target.value) })
              }
              fullWidth
            />
            <TextField
              label="Max Position Size"
              type="number"
              value={newBucket.max_position_size}
              onChange={(e) =>
                setNewBucket({ ...newBucket, max_position_size: parseFloat(e.target.value) })
              }
              fullWidth
              required
            />
            <TextField
              label="Stop Loss %"
              type="number"
              value={(newBucket.stop_loss_pct || 0) * 100}
              onChange={(e) =>
                setNewBucket({ ...newBucket, stop_loss_pct: parseFloat(e.target.value) / 100 })
              }
              fullWidth
              inputProps={{ step: 0.1 }}
            />
            <TextField
              label="Take Profit %"
              type="number"
              value={(newBucket.take_profit_pct || 0) * 100}
              onChange={(e) =>
                setNewBucket({
                  ...newBucket,
                  take_profit_pct: parseFloat(e.target.value) / 100,
                })
              }
              fullWidth
              inputProps={{ step: 0.1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateBucket}
            variant="contained"
            disabled={!newBucket.name || newBucket.allocated_capital <= 0}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      )}

      {/* Auto-Trading Tab */}
      {activeTab === 1 && (
        <Box>
          <AutoTrading />
        </Box>
      )}
    </Box>
  );
};

export default TradingDashboard;
